// src/features/files/hooks/useProfilePictureUpload.js
import { useState, useCallback } from 'react';
import { useFiles } from './useFiles';
import { useFolders } from './useFolders';

const PROFILE_PICTURES_FOLDER_NAME = 'i18n-files:i18n_folder_names.profile_pictures';

export function useProfilePictureUpload() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { uploadFile, setProfilePicture } = useFiles();
  const { getRootContents, createFolder } = useFolders();

  /**
   * Find or create the "Profile Pictures" folder
   * @returns {Promise<string>} folder_id
   */
  const ensureProfilePicturesFolder = useCallback(async () => {
    try {
      // Get root contents
      const rootResult = await getRootContents();
      
      if (!rootResult?.success) {
        throw new Error('Failed to fetch root contents');
      }

      const rootData = rootResult.data.data;
      console.log("ROOT DATA", rootData)
      
      // Look for existing "Profile Pictures" folder
      const existingFolder = rootData.folders?.find(
        folder => folder.folder_name === PROFILE_PICTURES_FOLDER_NAME
      );
      console.log("ROOT DATA", existingFolder)

      if (existingFolder) {
        return existingFolder.folder_id;
      }

      // Create the folder if it doesn't exist
      const createResult = await createFolder({
        folder_name: PROFILE_PICTURES_FOLDER_NAME,
        description: 'System folder for staff member profile pictures',
        parent_folder_id: null, // Root level
        is_system_folder: false,
        is_protected: false, // Profile pictures should be accessible
      });

      if (!createResult?.success) {
        throw new Error('Failed to create Profile Pictures folder');
      }

      return createResult.data.data.folder_id;
    } catch (err) {
      console.error('Error ensuring Profile Pictures folder:', err);
      throw err;
    }
  }, [getRootContents, createFolder]);

  /**
   * Upload profile picture for a staff member
   * @param {File} file - The image file to upload
   * @param {string} staffMemberId - The staff member's ID
   * @returns {Promise<Object>} Upload result
   */
  const uploadProfilePicture = useCallback(async (file, staffMemberId) => {
    setLoading(true);
    setError(null);

    try {
      // Ensure the Profile Pictures folder exists
      const folderId = await ensureProfilePicturesFolder();

      // Upload the file to the Profile Pictures folder
      const uploadResult = await uploadFile(file, {
        folder_id: folderId,
        is_system_file: false,
        is_protected: false, // Profile pictures should be publicly accessible
      });

      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || 'File upload failed');
      }

      const fileId = uploadResult.data.data.file_id;

      // Set it as the profile picture
      const setResult = await setProfilePicture(staffMemberId, fileId);

      if (!setResult?.success) {
        throw new Error(setResult?.error || 'Failed to set profile picture');
      }

      setLoading(false);
      return {
        success: true,
        data: setResult.data,
        fileId,
        folderId,
      };
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      setLoading(false);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [uploadFile, setProfilePicture, ensureProfilePicturesFolder]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    uploadProfilePicture,
    clearError,
  };
}